import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { C, mono, font } from "../config/theme";

/**
 * DrillDown — Tableau détaillé avec filtres colonnes type Excel + export Excel
 *
 * Props :
 *   - title    : titre affiché en haut du tableau
 *   - columns  : [{key, label, align?, mono?, bold?, color?, render?}]
 *   - rows     : données brutes (tableau d'objets)
 *   - color    : couleur d'accent (bordure top, boutons)
 */
export default function DrillDown({ title, columns, rows, color }) {
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const dropRef = useRef(null);

  /* Fermer le dropdown au clic extérieur */
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpenFilter(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Reset filtres quand les données changent */
  useEffect(() => {
    setColFilters({});
    setOpenFilter(null);
  }, [title]);

  if (!rows?.length) return null;

  /* Appliquer les filtres colonnes */
  const filteredRows = rows.filter((row) => {
    return Object.entries(colFilters).every(([key, vals]) => {
      if (!vals) return true;
      if (vals.size === 0) return false;
      const col = columns.find((c) => c.key === key);
      const cellVal = col?.render ? col.render(row) : row[key];
      const display = cellVal == null ? "—" : String(cellVal);
      return vals.has(display);
    });
  });

  /* Valeurs uniques d'une colonne (sur les données non filtrées) */
  const getUniqueVals = (col) => {
    const vals = new Set();
    rows.forEach((row) => {
      const cellVal = col.render ? col.render(row) : row[col.key];
      vals.add(cellVal == null ? "—" : String(cellVal));
    });
    return [...vals].sort((a, b) => a.localeCompare(b, "fr", { numeric: true }));
  };

  /* Toggle une valeur dans le filtre d'une colonne */
  const toggleVal = (colKey, val) => {
    setColFilters((prev) => {
      const current = prev[colKey] ? new Set(prev[colKey]) : new Set();
      if (current.has(val)) current.delete(val);
      else current.add(val);
      if (current.size === 0) {
        const next = { ...prev };
        delete next[colKey];
        return next;
      }
      return { ...prev, [colKey]: current };
    });
  };

  /* Tout cocher (= supprimer le filtre) / Tout décocher (= Set vide) */
  const toggleAll = (colKey, allVals) => {
    setColFilters((prev) => {
      const current = prev[colKey];
      if (!current || current.size === allVals.length) {
        return { ...prev, [colKey]: new Set() };
      }
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
  };

  /* Nombre de filtres actifs */
  const nbFilters = Object.keys(colFilters).length;

  /* Réinitialiser tous les filtres */
  const clearAll = () => {
    setColFilters({});
    setOpenFilter(null);
  };

  /* Export Excel des lignes filtrées */
  const handleExport = () => {
    const exportData = filteredRows.map((row) => {
      const obj = {};
      columns.forEach((c) => {
        const val = c.render ? c.render(row) : row[c.key];
        obj[c.label] = val == null ? "" : val;
      });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Export");
    ws["!cols"] = columns.map((c) => ({ wch: Math.max(c.label.length + 2, 15) }));
    XLSX.writeFile(
      wb,
      `${title.replace(/[^a-zA-Z0-9àâéèêëîïôùûüç\s-]/g, "").trim()}.xlsx`
    );
  };

  return (
    <div
      style={{
        background: C.surface,
        borderRadius: 12,
        border: `1px solid ${C.border}`,
        borderTop: `3px solid ${color}`,
        padding: "20px 24px",
        marginBottom: 24,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        overflow: "visible",
        animation: "fadeIn 200ms ease-out",
      }}
    >
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* En-tête : titre + compteur + actions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {nbFilters > 0 && (
            <button
              onClick={clearAll}
              style={{
                background: "none",
                border: "none",
                color,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: font,
                textDecoration: "underline",
              }}
            >
              Effacer les filtres ({nbFilters})
            </button>
          )}
          <div style={{ fontSize: 12, color: C.slate, fontFamily: mono }}>
            {filteredRows.length}
            {filteredRows.length !== rows.length ? ` / ${rows.length}` : ""} ligne
            {filteredRows.length > 1 ? "s" : ""}
          </div>
          <button
            onClick={handleExport}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "5px 12px",
              background: color,
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: font,
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            }}
          >
            <span style={{ fontSize: 13 }}>📥</span> Excel
          </button>
        </div>
      </div>

      {/* Tableau avec filtres colonnes */}
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12, fontFamily: font }}>
        <thead>
          <tr>
            {columns.map((c, i) => {
              const isOpen = openFilter === c.key;
              const hasFilter = !!colFilters[c.key];
              return (
                <th
                  key={i}
                  style={{
                    textAlign: c.align || "left",
                    padding: "8px 12px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.slate,
                    borderBottom: `2px solid ${C.border}`,
                    whiteSpace: "nowrap",
                    position: "relative",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => {
                    setOpenFilter(isOpen ? null : c.key);
                    setFilterSearch("");
                  }}
                >
                  <span>{c.label}</span>
                  <span style={{ marginLeft: 4, fontSize: 9, color: hasFilter ? color : C.slateLight }}>
                    {hasFilter ? "▼" : "▽"}
                  </span>

                  {/* Dropdown filtre colonne */}
                  {isOpen && (
                    <div
                      ref={dropRef}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        zIndex: 999,
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        padding: "8px 0",
                        minWidth: 200,
                        maxHeight: 320,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Barre de recherche */}
                      <div style={{ padding: "4px 10px 8px" }}>
                        <input
                          type="text"
                          placeholder="Rechercher..."
                          value={filterSearch}
                          onChange={(e) => setFilterSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            border: `1px solid ${C.border}`,
                            borderRadius: 6,
                            fontSize: 11,
                            outline: "none",
                            boxSizing: "border-box",
                            fontFamily: font,
                          }}
                          autoFocus
                        />
                      </div>
                      {/* Options checkboxes */}
                      <div style={{ overflowY: "auto", flex: 1, padding: "0 4px" }}>
                        {(() => {
                          const allVals = getUniqueVals(c);
                          const searched = filterSearch
                            ? allVals.filter((v) => v.toLowerCase().includes(filterSearch.toLowerCase()))
                            : allVals;
                          const currentSet = colFilters[c.key];
                          const allChecked = !currentSet;
                          return (
                            <>
                              {/* Tout sélectionner */}
                              <label
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "4px 8px",
                                  fontSize: 11,
                                  cursor: "pointer",
                                  color: C.text,
                                  fontWeight: 600,
                                  borderBottom: `1px solid ${C.border}`,
                                  marginBottom: 4,
                                  paddingBottom: 8,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAll(c.key, allVals);
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={allChecked}
                                  readOnly
                                  style={{ accentColor: color, cursor: "pointer" }}
                                />{" "}
                                Tout ({allVals.length})
                              </label>
                              {searched.map((val) => {
                                const checked = !currentSet || currentSet.has(val);
                                return (
                                  <label
                                    key={val}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      padding: "3px 8px",
                                      fontSize: 11,
                                      cursor: "pointer",
                                      color: C.text,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleVal(c.key, val);
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      readOnly
                                      style={{ accentColor: color, cursor: "pointer" }}
                                    />
                                    <span
                                      style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: 140,
                                      }}
                                    >
                                      {val}
                                    </span>
                                  </label>
                                );
                              })}
                              {searched.length === 0 && (
                                <div style={{ padding: "8px", fontSize: 11, color: C.slate, textAlign: "center" }}>
                                  Aucun résultat
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filteredRows.length > 0 ? (
            filteredRows.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? C.surface : C.bg }}>
                {columns.map((c, j) => (
                  <td
                    key={j}
                    style={{
                      padding: "8px 12px",
                      textAlign: c.align || "left",
                      fontFamily: c.mono ? mono : font,
                      fontWeight: c.bold ? 600 : 400,
                      color: c.color ? c.color(row) : C.text,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: "32px 12px", textAlign: "center", color: C.slate, fontSize: 13 }}
              >
                Aucune ligne ne correspond aux filtres sélectionnés
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
